import {
  CellClassParams,
  CellClassRules,
  ColDef,
  HeaderClassParams,
  IAggFunc,
  ICellRendererParams,
  IRowNode,
} from "@ag-grid-community/core";
import { AgGridReactProps } from "@ag-grid-community/react";
import { SystemStyleObject } from "@chakra-ui/styled-system";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableColumn } from "gen/model/genericTableColumn";
import { GenericTableRow } from "gen/model/genericTableRow";
import React from "react";
import {
  ClickableElementButtonType,
  ClickableElementLinkInfo,
} from "shared/components/clickable/ClickableElementLinkInfo";
import {
  DataTableColumnContext,
  DataTableContext,
} from "shared/components/table/data/DataTableContext";
import {
  ROW_ID_PAGINATION,
  ROW_ID_TOTAL,
} from "shared/components/table/data/internal/DataTableConstants";

export type RowSelectionState = Record<string, boolean>;
export type Updater<T> = T | ((old: T) => T);
export type OnChangeFn<T> = (updaterOrValue: Updater<T>) => void;

export type GenericTableAgGridReactProps = AgGridReactProps<GenericTableRow>;

export const isPaginationRow = (row: GenericTableRow | undefined) =>
  row?.id === ROW_ID_PAGINATION;

export const isTotalRow = (row: GenericTableRow | undefined) =>
  row?.id === ROW_ID_TOTAL;

export interface DataTableAgHeaderComponentParams {
  column: GenericTableColumn;
  parentColumns: GenericTableColumn[] | undefined;
}

export type DataTableHeaderOrCellClass = (
  params:
    | HeaderClassParams<GenericTableRow, GenericTableCell>
    | CellClassParams<GenericTableRow, GenericTableCell>,
) => string;

export type DataTableCellClassRules = CellClassRules<
  GenericTableRow,
  GenericTableCell
>;

export type DataTableAggFunc = IAggFunc<GenericTableRow, GenericTableCell>;

export type RenderHeaderValueProps = {
  type: "header";
  tableContext: DataTableContext;
  columnContext: DataTableColumnContext;
};

export type RenderCellValueProps = {
  type: "data";
  tableContext: DataTableContext;
  columnContext: DataTableColumnContext;
  row: IRowNode<GenericTableRow>;
  cell: GenericTableCell;
};

export type RenderCellValueFn = (
  props: RenderCellValueProps,
  fieldDisabled: boolean | undefined,
  params: ICellRendererParams,
) => React.ReactNode;

export type GetRowSpanKeyForCellFn = (
  props: RenderCellValueProps,
) => string | undefined;

export interface RenderRowActionsProps {
  row: IRowNode<GenericTableRow>;
}

export interface RenderRowActionsResult {
  type: "menu" | "buttons";
  actions: React.ReactNode | undefined;
}

export type RenderRowActionsFn = (
  props: RenderRowActionsProps,
) => RenderRowActionsResult | undefined;

export type GetColumnDefFn = (props: {
  column: GenericTableColumn;
  rootColumn?: GenericTableColumn;
}) => ColDef<GenericTableRow, GenericTableCell> | undefined;

export type StylesForCellFn = (
  props: RenderCellValueProps | RenderHeaderValueProps,
) => SystemStyleObject | undefined;

export type GetLinkInfoForCellFn = (
  props: RenderCellValueProps | RenderHeaderValueProps,
) => ClickableElementLinkInfo | undefined;

export type GetLinkInfoForRowFn = (props: {
  row: IRowNode<GenericTableRow>;
}) => ClickableElementLinkInfo | undefined;

export interface DataTableActionInfo {
  showEdit: boolean;
  showDelete: boolean;
}

export type GetActionsForCellFn = (
  props: RenderCellValueProps,
) => DataTableActionInfo | undefined;

export type GetActionsForColumnFn = (
  props: RenderHeaderValueProps,
) => DataTableActionInfo | undefined;

export interface DataTableOnButtonClickProps {
  tableContext: DataTableContext;
  columnContext: DataTableColumnContext;
  row?: IRowNode<GenericTableRow>;
  cell?: GenericTableCell;
  buttonType: ClickableElementButtonType;
  event: React.MouseEvent<HTMLElement>;
}

export type DataTableOnButtonClickFn = (
  props: DataTableOnButtonClickProps,
) => void;
